import { useState, useEffect, useRef, useCallback } from 'react';
import { View, Pressable, ScrollView, ActivityIndicator, TextInput } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { InputField, type InputFieldProps } from './InputField';
import { Card } from './Card';
import { ThemedText, Caption } from './Text';
import { useTheme } from '@/theme';

export type Suggestion = {
  value: string;
  label: string;
  subtitle?: string;
  icon?: string;
  metadata?: Record<string, unknown>;
};

export type AutocompleteInputProps = {
} & Omit<InputFieldProps, 'value' | 'onChangeText' | 'autoCapitalize'> & {
  value: string;
  onChangeText: (text: string) => void;
  onSelectSuggestion?: (suggestion: Suggestion) => void;
  suggestions: Suggestion[] | ((query: string) => Promise<Suggestion[]> | Suggestion[]);
  autoCapitalize?: 'none' | 'sentences' | 'words' | 'characters';
  minQueryLength?: number;
  debounceMs?: number;
  maxSuggestions?: number;
  showSuggestionsOnFocus?: boolean;
  filterSuggestions?: (query: string, suggestions: Suggestion[]) => Suggestion[];
  renderSuggestion?: (suggestion: Suggestion, onSelect: () => void) => React.ReactNode;
  /**
   * When true with `baselineValueForSkip`, skips async `suggestions(query)` until the user changes
   * the text away from the baseline (avoids heavy place search on modal open with a full address).
   */
  skipAsyncSuggestionsUntilEdited?: boolean;
  baselineValueForSkip?: string;
};

/**
 * Generic autocomplete input component with smart suggestions
 * Supports both static suggestions and async suggestion fetching
 */
export function AutocompleteInput({
  value,
  onChangeText,
  onSelectSuggestion,
  suggestions: suggestionsProp,
  placeholder,
  label,
  errorText,
  helperText,
  required,
  autoCapitalize = 'words',
  minQueryLength = 2,
  debounceMs = 300,
  maxSuggestions = 10,
  showSuggestionsOnFocus = false,
  filterSuggestions,
  renderSuggestion,
  skipAsyncSuggestionsUntilEdited = false,
  baselineValueForSkip,
  ...inputProps
}: AutocompleteInputProps) {
  const theme = useTheme();
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isSelectingRef = useRef(false);
  const inputRef = useRef<TextInput>(null);
  const [isFocused, setIsFocused] = useState(false);
  const userEditedSinceBaselineRef = useRef(false);
  // Tracks whether the dropdown is currently open.
  // We keep this ref in sync with state to avoid race conditions between focus/blur/search.
  const isShowingRef = useRef(false);

  const closeDropdown = useCallback(() => {
    isShowingRef.current = false;
    setShowSuggestions(false);
    // Clear suggestions to avoid reopening stale results on next focus.
    setSuggestions([]);
  }, []);

  // Get all suggestions (static or from function)
  const getAllSuggestions = async (query: string): Promise<Suggestion[]> => {
    if (typeof suggestionsProp === 'function') {
      return await suggestionsProp(query);
    }
    return suggestionsProp;
  };

  // Filter suggestions based on query
  const filterSuggestionsByQuery = (query: string, allSuggestions: Suggestion[]): Suggestion[] => {
    if (!query || query.trim().length < minQueryLength) {
      return [];
    }

    const normalizedQuery = query.trim().toLowerCase();
    
    // Use custom filter if provided
    if (filterSuggestions) {
      return filterSuggestions(normalizedQuery, allSuggestions);
    }

    // Default filtering: search in label, subtitle, and value
    const filtered = allSuggestions.filter((suggestion) => {
      const matchesLabel = suggestion.label.toLowerCase().includes(normalizedQuery);
      const matchesValue = suggestion.value.toLowerCase().includes(normalizedQuery);
      const matchesSubtitle = suggestion.subtitle?.toLowerCase().includes(normalizedQuery);
      return matchesLabel || matchesValue || matchesSubtitle;
    });

    // Sort by relevance (exact matches first, then starts with, then contains)
    return filtered
      .sort((a, b) => {
        const aLabel = a.label.toLowerCase();
        const bLabel = b.label.toLowerCase();
        const aStartsWith = aLabel.startsWith(normalizedQuery);
        const bStartsWith = bLabel.startsWith(normalizedQuery);
        const aExact = aLabel === normalizedQuery;
        const bExact = bLabel === normalizedQuery;

        if (aExact && !bExact) return -1;
        if (!aExact && bExact) return 1;
        if (aStartsWith && !bStartsWith) return -1;
        if (!aStartsWith && bStartsWith) return 1;
        return aLabel.localeCompare(bLabel);
      })
      .slice(0, maxSuggestions);
  };

  useEffect(() => {
    userEditedSinceBaselineRef.current = false;
  }, [baselineValueForSkip, skipAsyncSuggestionsUntilEdited]);

  // Search and update suggestions
  const searchSuggestions = async (query: string) => {
    if (!query || query.trim().length < minQueryLength) {
      setSuggestions([]);
      isShowingRef.current = false;
      setShowSuggestions(false);
      return;
    }

    if (
      skipAsyncSuggestionsUntilEdited &&
      baselineValueForSkip !== undefined &&
      query.trim() === baselineValueForSkip.trim() &&
      !userEditedSinceBaselineRef.current
    ) {
      setSuggestions([]);
      isShowingRef.current = false;
      setShowSuggestions(false);
      return;
    }

    // Don't show suggestions if user is selecting
    if (isSelectingRef.current) {
      return;
    }

    setLoading(true);
    try {
      const allSuggestions = await getAllSuggestions(query);
      const filtered = filterSuggestionsByQuery(query, allSuggestions);
      setSuggestions(filtered);
      // Only show suggestions if not selecting and we have results
      const shouldShow = filtered.length > 0 && !isSelectingRef.current && isFocused;
      isShowingRef.current = shouldShow;
      setShowSuggestions(shouldShow);
    } catch (error) {
      console.error('[AutocompleteInput] Error fetching suggestions:', error);
      setSuggestions([]);
      isShowingRef.current = false;
      setShowSuggestions(false);
    } finally {
      setLoading(false);
    }
  };

  // Debounced search
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      searchSuggestions(value);
    }, debounceMs);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [value, isFocused]);

  // Show suggestions on focus if enabled
  useEffect(() => {
    if (showSuggestionsOnFocus && isFocused && suggestions.length > 0 && !isSelectingRef.current && !showSuggestions) {
      isShowingRef.current = true;
      setShowSuggestions(true);
    }
  }, [showSuggestionsOnFocus, isFocused, suggestions.length, showSuggestions]);

  const handleChangeText = useCallback(
    (text: string) => {
      if (skipAsyncSuggestionsUntilEdited && baselineValueForSkip !== undefined) {
        if (text.trim() !== baselineValueForSkip.trim()) {
          userEditedSinceBaselineRef.current = true;
        }
      }
      onChangeText(text);
    },
    [baselineValueForSkip, onChangeText, skipAsyncSuggestionsUntilEdited],
  );

  const handleSelectSuggestion = (suggestion: Suggestion) => {
    isSelectingRef.current = true;
    // Clear blur timeout to prevent closing
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    // Close dropdown immediately - do this first
    closeDropdown();
    
    // Update the input value
    handleChangeText(suggestion.value);

    if (onSelectSuggestion) {
      onSelectSuggestion(suggestion);
    }

    // Reset selecting flag after a delay
    setTimeout(() => {
      isSelectingRef.current = false;
      // Blur the input to ensure dropdown stays closed
      inputRef.current?.blur();
    }, 100);
  };

  const handleFocus: NonNullable<InputFieldProps['onFocus']> = (e) => {
    setIsFocused(true);
    // If we already have suggestions (e.g. cached from typing), allow showing on focus.
    if (showSuggestionsOnFocus && suggestions.length > 0 && !showSuggestions) {
      isShowingRef.current = true;
      setShowSuggestions(true);
    }
    inputProps.onFocus?.(e);
  };

  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleBlur: NonNullable<InputFieldProps['onBlur']> = (e) => {
    setIsFocused(false);
    // Clear any existing timeout
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }
    // Delay hiding to allow selection - increased delay to prevent flickering
    blurTimeoutRef.current = setTimeout(() => {
      // If the input blurred, the dropdown should never remain open (unless we're in the middle of selecting).
      if (!isSelectingRef.current) closeDropdown();
      blurTimeoutRef.current = null;
    }, 300);
    inputProps.onBlur?.(e);
  };

  const defaultRenderSuggestion = (suggestion: Suggestion, onSelect: () => void) => (
    <Pressable
      key={suggestion.value}
      accessibilityRole="button"
      accessibilityLabel={`Select ${suggestion.label}${suggestion.subtitle ? `, ${suggestion.subtitle}` : ''}`}
      onPress={onSelect}
      style={({ pressed }) => ({
        padding: theme.spacing(1.5),
        borderRadius: theme.radii.md,
        backgroundColor: pressed ? theme.colors.borderMuted + '40' : 'transparent',
        minHeight: 44,
      })}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
        {suggestion.icon && (
          <Ionicons name={suggestion.icon as any} size={18} color={theme.colors.primary} />
        )}
        <View style={{ flex: 1 }}>
          <ThemedText variant="bodyStrong" style={{ fontSize: 15 }}>
            {suggestion.label}
          </ThemedText>
          {suggestion.subtitle && (
            <Caption color={theme.colors.textSecondary} style={{ fontSize: 12, marginTop: 2 }}>
              {suggestion.subtitle}
            </Caption>
          )}
        </View>
      </View>
    </Pressable>
  );

  return (
    <>
      <View>
        <InputField
          ref={inputRef}
          label={label}
          placeholder={placeholder}
          value={value}
          onChangeText={handleChangeText}
          errorText={errorText}
          helperText={helperText}
          required={required}
          autoCapitalize={autoCapitalize}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onSubmitEditing={(e) => {
            // If user hits "Next/Done", don't leave the dropdown hanging over the UI.
            closeDropdown();
            (inputProps as any).onSubmitEditing?.(e);
          }}
          editable={inputProps.editable !== false}
          {...inputProps}
        />
        
        {/* Loading indicator */}
        {loading && (
          <View style={{ 
            position: 'absolute', 
            right: theme.spacing(2), 
            top: label ? 40 : theme.spacing(1.5), 
            zIndex: 1 
          }}>
            <ActivityIndicator size="small" color={theme.colors.primary} />
          </View>
        )}
      </View>
      
      {/* Suggestions dropdown (inline) so it never blocks interacting with the rest of the form */}
      {showSuggestions && suggestions.length > 0 && (
        <Card
          variant="outlined"
          padding={0}
          style={{
            marginTop: theme.spacing(1),
            backgroundColor: theme.colors.surface,
          }}
        >
          <ScrollView
            keyboardShouldPersistTaps="handled"
            nestedScrollEnabled={true}
            style={{ maxHeight: 250 }}
          >
            <View style={{ gap: theme.spacing(0.5), padding: theme.spacing(1) }}>
              {suggestions.map((suggestion) => {
                const onSelect = () => handleSelectSuggestion(suggestion);
                if (renderSuggestion) {
                  return <View key={suggestion.value}>{renderSuggestion(suggestion, onSelect)}</View>;
                }
                return defaultRenderSuggestion(suggestion, onSelect);
              })}
            </View>
          </ScrollView>
        </Card>
      )}
    </>
  );
}

